import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ChevronDown, ChevronRight, FileText, Info, Loader2, Percent } from 'lucide-react';
import toast from 'react-hot-toast';
import { commissionApi } from '@/services/api';
import type { CommissionEntry, CommissionReport } from '@/types';
import { isCompanyAdmin, isManager } from '@/types';
import { useAuthStore } from '@/store/authStore';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import ReportDateRangeFilter from '@/components/reports/ReportDateRangeFilter';
import ReportExportButton from '@/components/reports/ReportExportButton';
import Pagination, { LIST_PAGE_SIZE } from '@/components/common/Pagination';
import Receipt from '@/components/pos/Receipt';
import { downloadCsv } from '@/utils/csv';
import { formatMoney } from '@/utils/currency';

type ViewMode = 'invoice' | 'item';

interface InvoiceCommissionGroup {
  key: string;
  pos_invoice: string;
  status: CommissionEntry['status'];
  invoice_datetime?: string;
  earner: string;
  earner_name?: string;
  invoice_sale_excl_tax?: number;
  total_commission: number;
  items: CommissionEntry[];
}

const STATUS_KEYS: Record<string, string> = {
  Accrued: 'commission.report.statusAccrued',
  Approved: 'commission.report.statusApproved',
  Paid: 'commission.report.statusPaid',
  Reversed: 'commission.report.statusReversed',
};

function groupEntriesByInvoiceAndStatus(entries: CommissionEntry[]): InvoiceCommissionGroup[] {
  const map = new Map<string, InvoiceCommissionGroup>();

  for (const entry of entries) {
    const key = `${entry.pos_invoice}|${entry.status}|${entry.earner}`;
    const existing = map.get(key);
    if (existing) {
      existing.items.push(entry);
      existing.total_commission += entry.commission_amount || 0;
      if (!existing.invoice_sale_excl_tax && entry.invoice_sale_excl_tax != null) {
        existing.invoice_sale_excl_tax = entry.invoice_sale_excl_tax;
      }
    } else {
      map.set(key, {
        key,
        pos_invoice: entry.pos_invoice,
        status: entry.status,
        invoice_datetime: entry.invoice_datetime,
        earner: entry.earner,
        earner_name: entry.earner_name,
        invoice_sale_excl_tax: entry.invoice_sale_excl_tax,
        total_commission: entry.commission_amount || 0,
        items: [entry],
      });
    }
  }

  return Array.from(map.values()).sort((a, b) => {
    const dateA = a.invoice_datetime ? new Date(a.invoice_datetime).getTime() : 0;
    const dateB = b.invoice_datetime ? new Date(b.invoice_datetime).getTime() : 0;
    return dateB - dateA;
  });
}

function isSelectableEntry(entry: CommissionEntry): boolean {
  return entry.commission_amount > 0 && entry.status !== 'Reversed';
}

export default function CommissionReportSection() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const role = useAuthStore((s) => s.role);
  const canManagePayout = isCompanyAdmin(role) || isManager(role);

  const {
    dateRange,
    setDateRange,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    from_date,
    to_date,
  } = useReportDateRange('This Month');

  const [earner, setEarner] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);
  const [invoiceName, setInvoiceName] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('invoice');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const { data: salesPeople = [] } = useQuery({
    queryKey: ['commissionSalesPeople'],
    queryFn: async () => {
      const res = await commissionApi.listSalesPeople();
      return res.data.message as { user: string; full_name: string }[];
    },
  });

  const { data: report, isLoading } = useQuery({
    queryKey: ['commissionReport', from_date, to_date, earner, status],
    queryFn: async () => {
      const res = await commissionApi.getReport({
        from_date,
        to_date,
        earner: earner || undefined,
        status: status || undefined,
      });
      return res.data.message as CommissionReport;
    },
  });

  const statusMutation = useMutation({
    mutationFn: ({ names, nextStatus }: { names: string[]; nextStatus: 'Approved' | 'Paid' | 'Accrued' }) =>
      commissionApi.updateStatus(names, nextStatus),
    onSuccess: (res) => {
      const updated = res.data.message?.updated ?? 0;
      if (updated > 0) {
        toast.success(t('commission.report.updated', { count: updated }));
      } else {
        toast.error(t('commission.report.noEligible'));
      }
      setSelected(new Set());
      void qc.invalidateQueries({ queryKey: ['commissionReport'] });
    },
    onError: () => toast.error(t('commission.report.updateFailed')),
  });

  const entries = report?.entries ?? [];
  const summary = report?.summary ?? [];
  const groups = useMemo(() => groupEntriesByInvoiceAndStatus(entries), [entries]);

  const totalCommission = useMemo(
    () => entries.reduce((sum, row) => sum + (row.commission_amount || 0), 0),
    [entries]
  );

  const displayRows = viewMode === 'invoice' ? groups : entries;
  const pageRows = displayRows.slice(page * LIST_PAGE_SIZE, (page + 1) * LIST_PAGE_SIZE);

  useEffect(() => {
    setPage(0);
    setSelected(new Set());
  }, [viewMode, from_date, to_date, earner, status, entries.length]);

  const translateStatus = (value: string) =>
    t(STATUS_KEYS[value] || value, { defaultValue: value });

  const selectableNames = useMemo(
    () => entries.filter(isSelectableEntry).map((e) => e.name),
    [entries]
  );

  const toggleSelect = (names: string[]) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const allSelected = names.every((n) => next.has(n));
      if (allSelected) {
        names.forEach((n) => next.delete(n));
      } else {
        names.filter((n) => selectableNames.includes(n)).forEach((n) => next.add(n));
      }
      return next;
    });
  };

  const toggleExpand = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const runStatusUpdate = (nextStatus: 'Approved' | 'Paid' | 'Accrued') => {
    const names = Array.from(selected);
    if (!names.length) return;
    statusMutation.mutate({ names, nextStatus });
  };

  const handleExport = () => {
    if (!entries.length) return;
    const rows =
      viewMode === 'invoice'
        ? groups.map((g) => ({
            invoice_datetime: g.invoice_datetime,
            pos_invoice: g.pos_invoice,
            earner_name: g.earner_name,
            // SD-UK-058: unique invoice lines (sale+return share the same item)
            item_count: new Set(g.items.map((e) => e.pos_invoice_item || e.item || e.name)).size,
            invoice_sale_excl_tax: g.invoice_sale_excl_tax,
            commission_amount: g.total_commission,
            status: g.status,
          }))
        : entries;

    downloadCsv(
      `commission-report-${viewMode}-${from_date}-${to_date}.csv`,
      viewMode === 'invoice'
        ? [
            { key: 'invoice_datetime', label: t('commission.report.date') },
            { key: 'pos_invoice', label: t('commission.report.invoice') },
            { key: 'earner_name', label: t('commission.report.earner') },
            { key: 'item_count', label: t('commission.report.item') },
            { key: 'invoice_sale_excl_tax', label: t('commission.report.saleExTax') },
            { key: 'commission_amount', label: t('commission.report.commission') },
            { key: 'status', label: t('commission.report.status') },
          ]
        : [
            { key: 'invoice_datetime', label: t('commission.report.date') },
            { key: 'pos_invoice', label: t('commission.report.invoice') },
            { key: 'item_name', label: t('commission.report.item') },
            { key: 'earner_name', label: t('commission.report.earner') },
            { key: 'rule_name', label: t('commission.report.rule') },
            { key: 'invoice_sale_excl_tax', label: t('commission.report.saleExTax') },
            { key: 'basis_amount', label: t('commission.report.basis') },
            { key: 'commission_rate', label: t('commission.report.rate') },
            { key: 'commission_amount', label: t('commission.report.commission') },
            { key: 'status', label: t('commission.report.status') },
          ],
      rows as Record<string, unknown>[]
    );
  };

  const renderStatusBadge = (value: string) => (
    <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
      {translateStatus(value)}
    </span>
  );

  const renderItemRow = (row: CommissionEntry, nested = false) => (
    <tr key={row.name} className={`hover:bg-gray-50/50 ${nested ? 'bg-gray-50/30' : ''}`}>
      {canManagePayout && (
        <td className="px-4 py-2">
          {isSelectableEntry(row) && (
            <input
              type="checkbox"
              checked={selected.has(row.name)}
              onChange={() => toggleSelect([row.name])}
            />
          )}
        </td>
      )}
      <td className={`px-4 py-2 whitespace-nowrap text-gray-500 ${nested ? 'pl-10' : ''}`}>
        {row.invoice_datetime
          ? format(new Date(row.invoice_datetime), 'd MMM yyyy')
          : row.creation
            ? format(new Date(row.creation), 'd MMM yyyy')
            : '—'}
      </td>
      <td className="px-4 py-2 whitespace-nowrap">
        {!nested ? (
          <button
            type="button"
            className="text-primary-600 hover:text-primary-700 font-medium"
            onClick={() => setInvoiceName(row.pos_invoice)}
          >
            {row.pos_invoice}
          </button>
        ) : (
          <span className="text-gray-400">{row.pos_invoice}</span>
        )}
      </td>
      {viewMode === 'item' && (
        <td className="px-4 py-2 text-gray-900">{row.item_name || row.rule_name || '—'}</td>
      )}
      <td className="px-4 py-2 text-gray-900">{row.earner_name || row.earner}</td>
      {viewMode === 'item' && (
        <td className="px-4 py-2 text-gray-600">{row.rule_name || '—'}</td>
      )}
      <td className="px-4 py-2 text-right text-gray-600">
        {row.invoice_sale_excl_tax != null && !nested ? formatMoney(row.invoice_sale_excl_tax) : nested ? '—' : row.invoice_sale_excl_tax != null ? formatMoney(row.invoice_sale_excl_tax) : '—'}
      </td>
      {viewMode === 'item' && (
        <>
          <td className="px-4 py-2 text-right text-gray-600">
            {row.basis_amount != null ? formatMoney(row.basis_amount) : '—'}
          </td>
          <td className="px-4 py-2 text-right text-gray-600">
            {row.commission_rate != null ? `${row.commission_rate}%` : '—'}
          </td>
        </>
      )}
      <td className={`px-4 py-2 text-right font-medium ${row.commission_amount < 0 ? 'text-red-600' : 'text-gray-900'}`}>
        {formatMoney(row.commission_amount)}
      </td>
      {viewMode === 'item' && <td className="px-4 py-2">{renderStatusBadge(row.status)}</td>}
    </tr>
  );

  return (
    <>
      <div className="card overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('commission.report.title')}</h2>
            <p className="text-sm text-gray-500 mt-1">{t('commission.report.subtitle')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/commission" className="btn-secondary inline-flex items-center gap-2 text-sm">
              <Percent className="w-4 h-4" />
              {t('commission.report.manageRules')}
            </Link>
            <div className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50">
              <button
                type="button"
                className={`px-3 py-1.5 text-sm rounded-md ${viewMode === 'invoice' ? 'bg-white shadow-sm font-medium text-gray-900' : 'text-gray-600'}`}
                onClick={() => setViewMode('invoice')}
              >
                {t('commission.report.viewInvoice')}
              </button>
              <button
                type="button"
                className={`px-3 py-1.5 text-sm rounded-md ${viewMode === 'item' ? 'bg-white shadow-sm font-medium text-gray-900' : 'text-gray-600'}`}
                onClick={() => setViewMode('item')}
              >
                {t('commission.report.viewItem')}
              </button>
            </div>
            <ReportExportButton onClick={handleExport} disabled={!entries.length} />
          </div>
        </div>

        {canManagePayout && (
          <div className="px-5 py-3 bg-blue-50 border-b border-blue-100 flex flex-wrap items-start gap-3 text-sm text-blue-900">
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            <p className="flex-1">{t('commission.report.approvalHint')}</p>
          </div>
        )}

        <div className="p-5 bg-gray-50/50 border-b border-gray-100 space-y-4">
          <ReportDateRangeFilter
            dateRange={dateRange}
            onDateRangeChange={(value) => {
              setDateRange(value);
              setPage(0);
            }}
            customFrom={customFrom}
            customTo={customTo}
            onCustomFromChange={setCustomFrom}
            onCustomToChange={setCustomTo}
          />

          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-1.5 flex-1 min-w-[180px]">
              <label className="text-sm font-medium text-gray-700">{t('commission.report.earner')}</label>
              <select
                className="input-field"
                value={earner}
                onChange={(e) => {
                  setEarner(e.target.value);
                  setPage(0);
                }}
              >
                <option value="">{t('commission.report.allEarners')}</option>
                {salesPeople.map((person) => (
                  <option key={person.user} value={person.user}>
                    {person.full_name || person.user}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5 flex-1 min-w-[160px]">
              <label className="text-sm font-medium text-gray-700">{t('commission.report.status')}</label>
              <select
                className="input-field"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(0);
                }}
              >
                <option value="">{t('commission.report.allStatuses')}</option>
                <option value="Accrued">{t('commission.report.statusAccrued')}</option>
                <option value="Approved">{t('commission.report.statusApproved')}</option>
                <option value="Paid">{t('commission.report.statusPaid')}</option>
                <option value="Reversed">{t('commission.report.statusReversed')}</option>
              </select>
            </div>
          </div>
        </div>

        {canManagePayout && selected.size > 0 && (
          <div className="px-5 py-3 border-b border-gray-100 bg-white flex flex-wrap items-center gap-3">
            <span className="text-sm text-gray-600">{t('commission.report.selected', { count: selected.size })}</span>
            <button
              type="button"
              className="btn-secondary text-sm"
              disabled={statusMutation.isPending}
              onClick={() => runStatusUpdate('Approved')}
            >
              {t('commission.report.approve')}
            </button>
            <button
              type="button"
              className="btn-secondary text-sm"
              disabled={statusMutation.isPending}
              onClick={() => runStatusUpdate('Paid')}
            >
              {t('commission.report.markPaid')}
            </button>
            <button
              type="button"
              className="btn-secondary text-sm"
              disabled={statusMutation.isPending}
              onClick={() => runStatusUpdate('Accrued')}
            >
              {t('commission.report.revertAccrued')}
            </button>
          </div>
        )}

        {summary.length > 0 && (
          <div className="p-5 border-b border-gray-100 bg-white">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('commission.report.summaryTitle')}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {summary.map((row) => (
                <div key={row.earner} className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
                  <p className="text-sm font-medium text-gray-900">{row.earner_name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {t('commission.report.entries', { count: row.entry_count })}
                  </p>
                  <p className="text-lg font-semibold text-primary-700 mt-1">{formatMoney(row.total_commission)}</p>
                </div>
              ))}
            </div>
            <p className="text-sm text-gray-600 mt-4">
              {t('commission.report.totalCommission')}:{' '}
              <span className="font-semibold text-gray-900">{formatMoney(totalCommission)}</span>
            </p>
          </div>
        )}

        <div className="overflow-x-auto max-h-[520px]">
          {isLoading ? (
            <div className="p-8 flex justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
          ) : displayRows.length > 0 ? (
            <table className="w-full text-sm text-left min-w-[980px]">
              <thead className="text-xs text-gray-500 bg-gray-50 uppercase sticky top-0 border-b border-gray-100">
                <tr>
                  {canManagePayout && (
                    <th className="px-4 py-3 font-medium w-10">
                      <input
                        type="checkbox"
                        checked={selectableNames.length > 0 && selectableNames.every((n) => selected.has(n))}
                        onChange={() => toggleSelect(selectableNames)}
                        title={t('commission.report.selectAll')}
                      />
                    </th>
                  )}
                  {viewMode === 'invoice' && <th className="px-4 py-3 w-8" />}
                  <th className="px-4 py-3 font-medium">{t('commission.report.date')}</th>
                  <th className="px-4 py-3 font-medium">{t('commission.report.invoice')}</th>
                  {viewMode === 'item' && <th className="px-4 py-3 font-medium">{t('commission.report.item')}</th>}
                  <th className="px-4 py-3 font-medium">{t('commission.report.earner')}</th>
                  {viewMode === 'item' && <th className="px-4 py-3 font-medium">{t('commission.report.rule')}</th>}
                  <th className="px-4 py-3 font-medium text-right">{t('commission.report.saleExTax')}</th>
                  {viewMode === 'item' && (
                    <>
                      <th className="px-4 py-3 font-medium text-right">{t('commission.report.basis')}</th>
                      <th className="px-4 py-3 font-medium text-right">{t('commission.report.rate')}</th>
                    </>
                  )}
                  {viewMode === 'invoice' && (
                    <th className="px-4 py-3 font-medium text-right">{t('commission.report.lineItems')}</th>
                  )}
                  <th className="px-4 py-3 font-medium text-right">{t('commission.report.commission')}</th>
                  <th className="px-4 py-3 font-medium">{t('commission.report.status')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {viewMode === 'item'
                  ? (pageRows as CommissionEntry[]).map((row) => (
                      <Fragment key={row.name}>
                        {renderItemRow(row)}
                      </Fragment>
                    ))
                  : (pageRows as InvoiceCommissionGroup[]).map((group) => {
                      const groupSelectable = group.items.filter(isSelectableEntry).map((i) => i.name);
                      const isExpanded = expanded.has(group.key);
                      return (
                        <Fragment key={group.key}>
                          <tr className="hover:bg-gray-50/50 bg-white">
                            {canManagePayout && (
                              <td className="px-4 py-3">
                                {groupSelectable.length > 0 && (
                                  <input
                                    type="checkbox"
                                    checked={groupSelectable.every((n) => selected.has(n))}
                                    onChange={() => toggleSelect(groupSelectable)}
                                  />
                                )}
                              </td>
                            )}
                            <td className="px-2 py-3">
                              <button
                                type="button"
                                className="p-1 rounded hover:bg-gray-100 text-gray-500"
                                onClick={() => toggleExpand(group.key)}
                                title={isExpanded ? t('commission.report.collapseItems') : t('commission.report.expandItems')}
                              >
                                {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                              </button>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-gray-500">
                              {group.invoice_datetime
                                ? format(new Date(group.invoice_datetime), 'd MMM yyyy')
                                : '—'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <button
                                type="button"
                                className="text-primary-600 hover:text-primary-700 font-medium"
                                onClick={() => setInvoiceName(group.pos_invoice)}
                              >
                                {group.pos_invoice}
                              </button>
                            </td>
                            <td className="px-4 py-3 text-gray-900">{group.earner_name || group.earner}</td>
                            <td className="px-4 py-3 text-right text-gray-600">
                              {group.invoice_sale_excl_tax != null ? formatMoney(group.invoice_sale_excl_tax) : '—'}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-600">
                              {t('commission.report.items', {
                                count: new Set(
                                  group.items.map((e) => e.pos_invoice_item || e.item || e.name),
                                ).size,
                              })}
                            </td>
                            <td
                              className={`px-4 py-3 text-right font-medium ${group.total_commission < 0 ? 'text-red-600' : 'text-gray-900'}`}
                            >
                              {formatMoney(group.total_commission)}
                            </td>
                            <td className="px-4 py-3">{renderStatusBadge(group.status)}</td>
                          </tr>
                          {isExpanded &&
                            group.items.map((item) => (
                              <tr key={item.name} className="bg-gray-50/40 text-sm">
                                {canManagePayout && (
                                  <td className="px-4 py-2">
                                    {isSelectableEntry(item) && (
                                      <input
                                        type="checkbox"
                                        checked={selected.has(item.name)}
                                        onChange={() => toggleSelect([item.name])}
                                      />
                                    )}
                                  </td>
                                )}
                                <td className="px-2 py-2" />
                                <td className="px-4 py-2 text-gray-400">—</td>
                                <td className="px-4 py-2 text-gray-400">—</td>
                                <td className="px-4 py-2 pl-8">
                                  <div className="text-gray-900">{item.item_name || '—'}</div>
                                  {item.rule_name && (
                                    <div className="text-xs text-gray-500">{item.rule_name}</div>
                                  )}
                                </td>
                                <td className="px-4 py-2 text-right text-gray-600">
                                  {item.basis_amount != null ? formatMoney(item.basis_amount) : '—'}
                                </td>
                                <td className="px-4 py-2 text-right text-gray-500">
                                  {item.commission_rate != null ? `${item.commission_rate}%` : '—'}
                                </td>
                                <td
                                  className={`px-4 py-2 text-right font-medium ${item.commission_amount < 0 ? 'text-red-600' : 'text-gray-700'}`}
                                >
                                  {formatMoney(item.commission_amount)}
                                </td>
                                <td className="px-4 py-2">{renderStatusBadge(item.status)}</td>
                              </tr>
                            ))}
                        </Fragment>
                      );
                    })}
              </tbody>
            </table>
          ) : (
            <div className="p-12 text-center">
              <FileText className="w-8 h-8 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">{t('commission.report.empty')}</p>
            </div>
          )}
        </div>

        {displayRows.length > 0 && (
          <div className="p-4 border-t border-gray-100 bg-gray-50">
            <Pagination page={page} pageSize={LIST_PAGE_SIZE} total={displayRows.length} onPageChange={setPage} />
          </div>
        )}
      </div>

      {invoiceName && <Receipt invoiceName={invoiceName} onClose={() => setInvoiceName('')} />}
    </>
  );
}
